// The microphone, camera and speaker this device uses for calls.
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { devicesOf, loadChoice, resolveDevice, saveChoice, type AvChoice, type AvDevice, type AvKind } from '../lib/avDevices'
import { useCall } from './call'

type WithSelectOutput = MediaDevices & { selectAudioOutput?: () => Promise<MediaDeviceInfo> }

export const useAv = defineStore('av', () => {
  const raw = shallowRef<MediaDeviceInfo[]>([])
  const choice = ref<AvChoice>(loadChoice())
  const md = navigator.mediaDevices as WithSelectOutput | undefined

  const mics = computed(() => devicesOf(raw.value, 'audioinput'))
  const cameras = computed(() => devicesOf(raw.value, 'videoinput'))
  const speakers = computed(() => devicesOf(raw.value, 'audiooutput'))
  const lists: Record<AvKind, () => AvDevice[]> = {
    audioinput: () => mics.value,
    videoinput: () => cameras.value,
    audiooutput: () => speakers.value,
  }
  /** Device labels (and real ids) only appear once the site may use the mic or camera. */
  const permitted = computed(() => raw.value.some((d) => d.kind !== 'audiooutput' && d.label))
  /** Safari and older browsers can't route audio to a chosen speaker. */
  const outputSelectable = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype
  /** Firefox lists speakers only after the user picks one through its own prompt. */
  const outputPrompt = computed(() => outputSelectable && speakers.value.length === 0 && !!md?.selectAudioOutput)

  async function refresh(): Promise<void> {
    if (!md?.enumerateDevices) return
    raw.value = await md.enumerateDevices().catch(() => [])
  }
  md?.addEventListener?.('devicechange', () => void refresh())

  function current(kind: AvKind): string | undefined {
    return resolveDevice(lists[kind](), choice.value[kind])
  }

  async function requestAccess(): Promise<void> {
    if (!md?.getUserMedia) return
    const s = await md.getUserMedia({ audio: true, video: true }).catch(() => md.getUserMedia({ audio: true }))
    s.getTracks().forEach((t) => t.stop())
    await refresh()
  }

  async function choose(kind: AvKind, id: string): Promise<void> {
    const label = lists[kind]().find((d) => d.id === id)?.label ?? ''
    choice.value = { ...choice.value, [kind]: { id, label } }
    saveChoice(choice.value)
    await useCall().switchDevice(kind, id)
  }

  async function promptForSpeaker(): Promise<void> {
    const d = await md?.selectAudioOutput?.()
    if (!d) return
    await refresh()
    if (!raw.value.some((x) => x.deviceId === d.deviceId)) raw.value = [...raw.value, d]
    await choose('audiooutput', d.deviceId)
  }

  return { mics, cameras, speakers, choice, permitted, outputSelectable, outputPrompt, refresh, current, requestAccess, choose, promptForSpeaker }
})
